import { num } from "@/lib/format";

export const ordinal = (n) => `${n}° tagliato`;

export const requiredBySize = (rows, sizes) => Object.fromEntries(sizes.map((z) => [z, rows.reduce((t, r) => t + (r.sizes.find((x) => x.size === z)?.quantity || 0), 0)]));

export const markerMeters = (m) => (Number(m.length_m) || 0) * (Number(m.layers) || 0);

export const markerPieces = (m, z) => (m.free_sizes ? 0 : (Number(m.sizes?.[z]) || 0) * (Number(m.layers) || 0));

export const markerSizesLabel = (m, sizes) => (m.free_sizes ? "Tutte le taglie" : sizes.filter((z) => Number(m.sizes?.[z]) > 0).map((z) => (Number(m.sizes[z]) > 1 ? `${Number(m.sizes[z])}×${z}` : z)).join(" + ") || "-");

// Adesivati nascosti per ora nella scheda taglio
export const fabricLabel = (m) => m.fabric?.trim() || "Senza tessuto";

export const fabricGroups = (markers) => {
  const out = {};
  markers.forEach((m) => {
    const k = fabricLabel(m);
    out[k] = out[k] || { label: k, fabric: m.fabric?.trim() || "", fused: false, meters: 0 };
    out[k].meters += markerMeters(m);
  });
  return Object.values(out);
};

export const fabricTotals = (markers) => fabricGroups(markers).map((g) => [g.label, g.meters]);

export const colorMeters = (m, c) => (Number(m.length_m) || 0) * (Number(m.layers_by_color?.[c]) || 0);

// Metri per tessuto e colore: quanto scalare e di quale tessuto
export const fabricColorTotals = (markers, colors) => {
  const out = {};
  markers.forEach((m) => colors.forEach((c) => {
    const k = `${fabricLabel(m)}|${c}`;
    out[k] = out[k] || { fabric: fabricLabel(m), color: c, meters: 0 };
    out[k].meters += colorMeters(m, c);
  }));
  return Object.values(out).filter((r) => r.meters > 0);
};

export const metersLabel = (n) => `${num(Math.round(n * 100) / 100)} m`;

export const blankMarker = (fabric = "") => ({ fabric, fused: false, sizes: {}, length_m: "", layers: "", layers_by_color: {}, layers_edited: [] });

// Strati per colore calcolati dai capi da tagliare; i colori modificati a mano restano come scritti
export const withAutoLayers = (markers, lines) => {
  const rem = {};
  return markers.map((m) => {
    const f = (m.fabric || "").trim().toLowerCase();
    const zs = m.free_sizes ? [] : Object.entries(m.sizes || {}).filter(([, n]) => Number(n) > 0);
    const edited = m.layers_edited || [];
    const lbc = {};
    lines.filter((l) => l.color.trim()).forEach((l) => {
      const c = l.color.trim();
      const key = `${f}|${c.toLowerCase()}`;
      if (!rem[key]) rem[key] = Object.fromEntries(Object.entries(l.sizes || {}).map(([z, v]) => [z, Number(v) || 0]));
      const r = rem[key];
      const auto = zs.length ? Math.max(0, ...zs.map(([z, n]) => Math.ceil((r[z] || 0) / Number(n)))) : 0;
      const v = m.free_sizes || edited.includes(c) ? m.layers_by_color?.[c] ?? "" : auto;
      if (m.free_sizes && Number(v) > 0) Object.keys(r).forEach((z) => { r[z] = 0; });
      zs.forEach(([z, n]) => { r[z] = Math.max(0, (r[z] || 0) - (Number(v) || 0) * Number(n)); });
      lbc[c] = v;
    });
    return { ...m, layers_by_color: lbc, layers: String(layersTotal(lbc)) };
  });
};

export const layersTotal = (lbc = {}) => Object.values(lbc).reduce((t, v) => t + (Number(v) || 0), 0);

export const layersLabel = (m) => {
  const e = Object.entries(m.layers_by_color || {}).filter(([, v]) => Number(v) > 0);
  return e.length ? e.map(([c, v]) => `${v} ${c}`).join(" · ") : "";
};

// Righe colore × taglia della scheda taglio (le vecchie schede le prendono dai lotti)
export const ticketLines = (t) => t.lines?.length ? t.lines.map((l) => ({ color: l.color, sizes: { ...l.sizes }, fabrics: { ...(l.fabrics || {}) } }))
  : (t.rows || []).map((r) => ({ color: r.color || "", sizes: Object.fromEntries((r.sizes || []).map((s) => [s.size, s.quantity])), fabrics: {} }));

// Tessuti del magazzino per tipo e colore: se ce n'è uno solo lo prende in automatico
// Tessuti del tipo scritto in scheda; se il testo è più lungo (es. "Grisaglia ART FLAMING") cerco tipo e nome contenuti nel testo
export const fabricMatches = (mats, type) => {
  const t = (type || "").trim().toLowerCase();
  const fab = mats.filter((m) => m.type === "tessuto");
  const exact = fab.filter((m) => (m.fabric_type || "").toLowerCase() === t || (m.name || "").toLowerCase() === t);
  if (exact.length || !t) return exact;
  const has = (x) => x && t.includes(x.trim().toLowerCase());
  const byType = fab.filter((m) => has(m.fabric_type));
  const both = byType.filter((m) => has(m.name));
  return both.length ? both : byType.length ? byType : fab.filter((m) => has(m.name));
};

export const fabricOptions = (mats, type, color) => {
  const all = fabricMatches(mats, type);
  return { all, same: all.filter((m) => (m.color || "").toLowerCase() === (color || "").trim().toLowerCase()) };
};

// Tessuto dalla distinta della scheda tecnica per quel colore, se esiste ancora in magazzino
export const bomFabric = (bom, color, ty, mats) => {
  const id = bom?.[(color || "").trim().toLowerCase()]?.[ty];
  return id && mats.some((m) => m.id === id) ? id : "";
};

export const resolveFabrics = (line, types, mats, bom) => Object.fromEntries(types.map((ty) => {
  if (line.fabrics?.[ty]) return [ty, line.fabrics[ty]];
  const b = bomFabric(bom, line.color, ty, mats);
  if (b) return [ty, b];
  const { same } = fabricOptions(mats, ty, line.color);
  return [ty, same.length === 1 ? same[0].id : ""];
}));

export const linesToRows = (lines, sizes) => lines.filter((l) => l.color.trim()).map((l) => {
  const sz = sizes.filter((z) => Number(l.sizes[z]) > 0).map((z) => ({ size: z, quantity: Number(l.sizes[z]) }));
  return { batch_id: l.color, color: l.color, sizes: sz, quantity: sz.reduce((a, s) => a + s.quantity, 0) };
});

export const ticketPayload = (t) => ({
  sheet_id: t.sheet_id || null, code: t.code || null, name: t.sheet_name || null, season: t.season || null, sizes: t.sizes || [],
  lines: t.lines.map((l) => ({ color: l.color.trim(), fabrics: l.fabrics || {}, sizes: Object.fromEntries(Object.entries(l.sizes).map(([k, v]) => [k, Number(v) || 0])) })),
  notes: t.notes || "", attention: t.attention || "",
  markers: t.markers.map((m) => ({
    fabric: m.fabric || "", fused: !!m.fused, length_m: m.length_m === "" ? null : Number(m.length_m), layers: m.layers === "" ? null : Number(m.layers),
    layers_by_color: Object.fromEntries(Object.entries(m.layers_by_color || {}).map(([k, v]) => [k, Number(v) || 0])),
    layers_edited: m.layers_edited || [],
    free_sizes: !!m.free_sizes,
    sizes: Object.fromEntries(Object.entries(m.sizes || {}).map(([k, v]) => [k, Number(v) || 0])),
  })),
});
